if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.variantData = this.getVariantData();

        this.init();
        this.bindStickyControls();

        if (this.submitEl) {
          this.submitEl.addEventListener('click', this.onClick.bind(this));
        }

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      onClick() {
        this.syncMainQuantity();
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.resetSubmitButton(this.submitEl);
      }

      resetSubmitButton(button) {
        if (!button) return;

        button.classList.remove('btn--loading');
        button.removeAttribute('aria-disabled');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('.btn__text');
        const icon = addButtonText?.querySelector('svg');
        if (icon && !this.buttonIconMarkup) {
          this.buttonIconMarkup = icon.outerHTML;
        }
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) {
            addButtonText.innerHTML = `${this.buttonIconMarkup || ''}${text}`;
          }
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) {
            addButtonText.innerHTML = `${this.buttonIconMarkup || ''}${FoxTheme.variantStrings.addToCart}`;
          }
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      bindStickyControls() {
        this.querySelectorAll('[data-sticky-option-value]').forEach((button) => {
          button.addEventListener('click', () => {
            if (button.getAttribute('aria-disabled') === 'true') return;
            this.selectMainOption(button.dataset.stickyOptionValue);
            this.updateSelectedSize(button.dataset.stickyOptionValue);
          });
        });

        this.querySelectorAll('.sticky-atc-bar__size-chart[aria-controls]').forEach((button) => {
          button.addEventListener('click', (event) => {
            const modal = document.getElementById(button.getAttribute('aria-controls'));
            if (!modal || typeof modal.show !== 'function') return;

            event.preventDefault();
            modal.show(button);
          });
        });

        const stickyQuantity = this.querySelector('.sticky-atc-bar__quantity');
        if (stickyQuantity) {
          stickyQuantity.addEventListener('change', () => this.syncMainQuantity());
          stickyQuantity.addEventListener('input', () => this.syncMainQuantity());
          stickyQuantity.querySelectorAll('button').forEach((button) => {
            button.addEventListener('click', () => {
              window.setTimeout(() => this.syncMainQuantity(), 0);
            });
          });
        }
      }

      getVariantData() {
        const variantsJson = this.querySelector('[data-sticky-variants]');
        if (!variantsJson) return [];

        try {
          return JSON.parse(variantsJson.textContent);
        } catch (error) {
          return [];
        }
      }

      get mainQuantityInput() {
        return document.querySelector(`#Quantity-${this.dataset.section}`);
      }

      get stickyQuantityInput() {
        return this.querySelector('.sticky-atc-bar__quantity .quantity__input');
      }

      selectMainOption(value) {
        if (!this.mainProductInfo) return;

        const optionPosition = Number(this.querySelector('[data-sticky-option-position]')?.dataset.stickyOptionPosition);
        const optionGroups = this.mainProductInfo.querySelectorAll('variant-selects .product-form__input');
        const optionGroup = optionGroups[optionPosition - 1];
        if (!optionGroup) return;

        const matchingSelect = optionGroup.querySelector('select');
        if (matchingSelect) {
          if (matchingSelect.value === value) return;
          matchingSelect.value = value;
          matchingSelect.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const matchingInput = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === value
        );
        if (!matchingInput || matchingInput.checked) return;

        matchingInput.click();
      }

      syncMainQuantity() {
        const mainInput = this.mainQuantityInput;
        const stickyInput = this.stickyQuantityInput;
        if (!mainInput || !stickyInput) return;

        mainInput.value = stickyInput.value;
        mainInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateSelectedSize(value) {
        this.querySelectorAll('[data-sticky-option-value]').forEach((button) => {
          button.classList.toggle('is-selected', button.dataset.stickyOptionValue === value);
        });
      }

      getStickyOptionIndex() {
        const optionPosition = Number(this.querySelector('[data-sticky-option-position]')?.dataset.stickyOptionPosition);
        return Number.isNaN(optionPosition) ? 0 : optionPosition - 1;
      }

      updateStock(variant) {
        const stock = this.querySelector('[data-sticky-stock]');
        const stockText = this.querySelector('[data-sticky-stock-text]');
        if (!stock || !stockText || !variant) return;

        stock.classList.toggle('sticky-atc-bar__stock--sold-out', !variant.available);
        stock.classList.toggle('sticky-atc-bar__stock--in-stock', variant.available);

        if (!variant.available) {
          stockText.textContent = FoxTheme.variantStrings.soldOut;
          return;
        }

        if (
          variant.inventory_management === 'shopify' &&
          Number(variant.inventory_quantity) <= 0 &&
          variant.inventory_policy === 'continue'
        ) {
          stockText.textContent = 'In stock (can be backordered)';
          return;
        }

        stockText.textContent = 'In stock';
      }

      updateQuantity(variant) {
        const stickyInput = this.stickyQuantityInput;
        if (!stickyInput || !variant) return;

        const quantityRule = variant.quantity_rule || {};
        const min = quantityRule.min || 1;
        const max = quantityRule.max;
        const increment = quantityRule.increment || 1;

        stickyInput.min = min;
        stickyInput.dataset.min = min;
        stickyInput.step = increment;
        if (max) {
          stickyInput.max = max;
          stickyInput.dataset.max = max;
        } else {
          stickyInput.removeAttribute('max');
          stickyInput.removeAttribute('data-max');
        }
        if (Number(stickyInput.value) < min) {
          stickyInput.value = min;
        }
        stickyInput.dataset.quantityVariantId = variant.id;
        this.syncMainQuantity();
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo) return;
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          if (!variant) {
            this.updateButton(true, '', true);
            return;
          }
          this.updateSelectedSize(variant.options?.[this.getStickyOptionIndex()]);
          this.updateStock(variant);
          this.updateQuantity(variant);
          this.updateButton(true, '', false);
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
        });
      }
    }
  );
}
